import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Settings, Cookie } from "lucide-react";
import { Link } from "react-router-dom";

type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
};

const defaultPreferences: CookiePreferences = {
  necessary: true, // Always true, cannot be disabled
  analytics: false,
  functional: false,
};

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    }

    // Listen for footer "Configurar Cookies" click
    const handleOpenCookieSettings = () => {
      setShowSettings(false);
      setPreferences(defaultPreferences);
      setIsVisible(true);
    };

    window.addEventListener("openCookieSettings", handleOpenCookieSettings);
    return () => {
      window.removeEventListener("openCookieSettings", handleOpenCookieSettings);
    };
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem("cookie_consent", JSON.stringify({
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: "1.0"
    }));
    setIsVisible(false);
  };

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      functional: true,
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const rejectNonEssential = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      functional: false,
    };
    setPreferences(onlyNecessary);
    saveConsent(onlyNecessary);
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === "necessary") return; // Cannot disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 pointer-events-none">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-foreground/20 backdrop-blur-sm pointer-events-auto"
        onClick={() => {}}
      />
      
      {/* Banner */}
      <div className="relative w-full max-w-2xl bg-card rounded-2xl shadow-2xl border border-border overflow-hidden pointer-events-auto animate-fade-in-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-muted/50">
          <div className="flex items-center gap-2">
            <Cookie className="w-5 h-5 text-accent" />
            <h3 className="font-semibold text-foreground">Configuración de Cookies</h3>
          </div>
          <button 
            onClick={rejectNonEssential}
            className="p-1 hover:bg-muted rounded-md transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6">
          {!showSettings ? (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Utilizamos cookies para mejorar tu experiencia en nuestra web. Algunas son esenciales 
                para el funcionamiento del sitio, mientras que otras nos ayudan a analizar el tráfico 
                y personalizar contenido. Puedes aceptar todas, rechazar las no esenciales o 
                configurar tus preferencias.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                Más información en nuestra{" "}
                <Link to="/cookies" className="text-accent hover:underline">
                  Política de Cookies
                </Link>
                {" "}y{" "}
                <Link to="/privacidad" className="text-accent hover:underline">
                  Política de Privacidad
                </Link>.
              </p>
            </>
          ) : (
            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex-1 pr-4">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-foreground text-sm">Cookies necesarias</h4>
                    <span className="text-[10px] px-1.5 py-0.5 bg-accent/20 text-accent rounded">Requeridas</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Esenciales para el funcionamiento del sitio. No pueden desactivarse.
                  </p>
                </div>
                <div className="flex items-center h-6">
                  <div className="w-10 h-5 bg-accent rounded-full relative">
                    <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full" />
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex-1 pr-4">
                  <h4 className="font-medium text-foreground text-sm">Cookies de análisis</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Nos ayudan a entender cómo interactúas con el sitio para mejorarlo.
                  </p>
                </div>
                <button 
                  onClick={() => togglePreference("analytics")}
                  className={`w-10 h-5 rounded-full relative transition-colors ${
                    preferences.analytics ? "bg-accent" : "bg-muted-foreground/30"
                  }`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${
                    preferences.analytics ? "right-0.5" : "left-0.5"
                  }`} />
                </button>
              </div>

              {/* Functional Cookies */}
              <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex-1 pr-4">
                  <h4 className="font-medium text-foreground text-sm">Cookies funcionales</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Permiten recordar tus preferencias como idioma y tema visual.
                  </p>
                </div>
                <button 
                  onClick={() => togglePreference("functional")}
                  className={`w-10 h-5 rounded-full relative transition-colors ${
                    preferences.functional ? "bg-accent" : "bg-muted-foreground/30"
                  }`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${
                    preferences.functional ? "right-0.5" : "left-0.5"
                  }`} />
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2">
            {!showSettings ? (
              <>
                <Button 
                  variant="hero" 
                  size="sm" 
                  onClick={acceptAll}
                  className="flex-1"
                >
                  Aceptar todas
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={rejectNonEssential}
                  className="flex-1"
                >
                  Rechazar no esenciales
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowSettings(true)}
                  className="flex-1"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Configurar
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="hero" 
                  size="sm" 
                  onClick={savePreferences}
                  className="flex-1"
                >
                  Guardar preferencias
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowSettings(false)}
                  className="flex-1"
                >
                  Volver
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
